/**
 * Approval gap flags — keep aligned with `src/services/stagedProductApprovalRules.ts` (getStagedApprovalGaps).
 */

import { catalogContentConsistencyIssues } from './catalogContentConsistency';
import { fujifilmCatalogGap } from './fujifilmProductCodes';
import { identifierReviewIssueMessages } from './identifierReviewNotes';
import { isShortDescriptionIssueField } from './shortDescription';
import { looksLikeSiteChrome } from './siteChromeText';

export type StagedApprovalGapId =
  | 'sku'
  | 'upc'
  | 'category'
  | 'productType'
  | 'price'
  | 'weight'
  | 'photo'
  | 'shortDescription'
  | 'longDescription'
  | 'features'
  | 'fujifilmCatalog'
  | 'verificationError'
  | 'identifierReview'
  | 'contentConsistency';

type StagedLike = {
  brand?: string;
  brandId?: string;
  mpn?: string;
  sku?: string;
  upc?: string;
  ean?: string;
  gtin?: string;
  skuSource?: string;
  source?: { sourceUrl?: string };
  acknowledgedReviewFlags?: Record<string, { by: string; at: Date | string; value: string }>;
  category?: string;
  productType?: string;
  name?: unknown;
  model?: unknown;
  specifications?: unknown;
  specificationsRaw?: unknown;
  inTheBox?: unknown;
  pricing?: {
    msrp?: number;
    map?: number;
    retail?: number;
    salePrice?: number;
  };
  weight?: { value?: number };
  shippingWeight?: { value?: number };
  images?: Array<{ url?: string }>;
  /** Gemini / import payloads sometimes store objects here instead of strings. */
  description?: unknown;
  aiDescription?: unknown;
  shortDescription?: unknown;
  aiShortDescription?: unknown;
  features?: unknown;
  aiFeatures?: unknown;
  highlights?: unknown;
  featureSections?: unknown;
  verification?: {
    issues?: Array<{ severity?: string; message?: string; field?: string }>;
  };
};

function stripHtml(html: string): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Coerce catalog/AI text fields; ignore non-strings (bad Gemini/object payloads). */
function asTrimmedText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).trim();
  return '';
}

function firstTrimmedText(...candidates: unknown[]): string {
  for (const c of candidates) {
    const t = asTrimmedText(c);
    if (t && !looksLikeSiteChrome(t)) return t;
  }
  return '';
}

function countSentences(text: string): number {
  const t = stripHtml(text);
  if (!t) return 0;
  const parts = t.split(/(?<=[.!?])\s+/).map((p) => p.trim()).filter((p) => p.length > 0);
  if (parts.length > 1) return parts.length;
  const fallback = t.split(/[.!?]+/).map((p) => p.trim()).filter((p) => p.length > 0);
  return fallback.length;
}

function effectiveLong(s: StagedLike): string {
  return firstTrimmedText(s.aiDescription, s.description);
}

function effectiveShort(s: StagedLike): string {
  return firstTrimmedText(s.aiShortDescription, s.shortDescription);
}

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((x) => {
    const t = String(x ?? '').trim();
    return t.length > 0 && !looksLikeSiteChrome(t);
  });
}

function featureItemCount(s: StagedLike): number {
  const lines = [...asStringList(s.aiFeatures || s.features), ...asStringList(s.highlights)];
  let n = lines.length;
  for (const sec of Array.isArray(s.featureSections) ? s.featureSections : []) {
    const text = `${String(sec.title || '').trim()} ${String(sec.body || '').trim()}`.trim();
    if (text && !looksLikeSiteChrome(text)) n += 1;
  }
  return n;
}

function hasPositivePrice(s: StagedLike): boolean {
  const p = s.pricing;
  if (!p) return false;
  const nums = [p.msrp, p.map, p.retail, p.salePrice].filter(
    (v) => v != null && typeof v === 'number' && Number.isFinite(v) && (v as number) > 0,
  );
  return nums.length > 0;
}

function hasPhoto(s: StagedLike): boolean {
  return (s.images || []).some((i) => i?.url && String(i.url).trim().length > 0);
}

function isPositiveWeight(w?: { value?: number }): boolean {
  if (!w || w.value == null) return false;
  return typeof w.value === 'number' && Number.isFinite(w.value) && w.value > 0;
}

/**
 * Mirrors `hasWeight` in the API's stagedProductApprovalRules: a boxed weight
 * satisfies the bar and is checked first, because the requirement exists so the
 * ERP can quote carrier rates and the Shopify sync already prefers the shipping
 * weight for variants.
 */
function hasWeight(s: StagedLike): boolean {
  return isPositiveWeight(s.shippingWeight) || isPositiveWeight(s.weight);
}

function verificationIssueFieldTop(field?: string): string {
  return String(field || '')
    .toLowerCase()
    .trim()
    .split(/[\s._/-]+/)
    .find(Boolean) || '';
}

/**
 * Keep aligned with `isBlockingVerificationIssue` in
 * `src/services/stagedProductApprovalRules.ts`. Category / description /
 * image quality findings are not identity blockers.
 */
function isBlockingVerificationIssue(issue: {
  severity?: string;
  message?: string;
  field?: string;
}): boolean {
  const message = String(issue.message || '');
  if (/critical\s+mismatch/i.test(message)) return true;

  const severity = String(issue.severity || '').toLowerCase();
  if (severity !== 'error' && severity !== 'critical') return false;

  if (isShortDescriptionIssueField(String(issue.field || ''))) return false;

  const top = verificationIssueFieldTop(issue.field);
  const qualityTops = new Set([
    'description',
    'features',
    'image',
    'images',
    'weight',
    'category',
    'producttype',
    'pricing',
    'price',
    'spec',
    'specs',
    'specifications',
    'short',
  ]);
  if (!top || qualityTops.has(top) || /length|count|completeness|quality/i.test(String(issue.field || ''))) {
    return false;
  }

  return ['name', 'model', 'sku', 'upc', 'ean', 'gtin', 'mpn', 'brand'].includes(top);
}

function hasBlockingVerificationIssues(s: StagedLike): boolean {
  return (s.verification?.issues || []).some(isBlockingVerificationIssue);
}

/** True when the row has a barcode that satisfies approve/promote (UPC or EAN). */
export function stagedHasBarcode(p: StagedLike): boolean {
  return Boolean(String(p.upc || '').trim() || String(p.ean || '').trim());
}

/** Which approval requirements this staged row is missing (for filters / sorting). */
export function getStagedApprovalGaps(p: StagedLike): StagedApprovalGapId[] {
  const gaps: StagedApprovalGapId[] = [];
  if (!String(p.sku || '').trim()) gaps.push('sku');
  if (!stagedHasBarcode(p)) gaps.push('upc');
  if (!p.category || !String(p.category).trim()) gaps.push('category');
  if (!p.productType || !String(p.productType).trim()) gaps.push('productType');
  if (!hasPositivePrice(p)) gaps.push('price');
  if (!hasWeight(p)) gaps.push('weight');
  if (!hasPhoto(p)) gaps.push('photo');
  if (!effectiveShort(p)) gaps.push('shortDescription');
  if (countSentences(effectiveLong(p)) <= 3) gaps.push('longDescription');
  if (featureItemCount(p) <= 3) gaps.push('features');
  if (fujifilmCatalogGap(p.brand, p.brandId, p.sku, p.mpn)) gaps.push('fujifilmCatalog');
  if (
    identifierReviewIssueMessages({
      brand: p.brand,
      brandId: p.brandId,
      sku: p.sku,
      mpn: p.mpn,
      upc: p.upc,
      ean: p.ean,
      gtin: p.gtin,
      sourceUrl: p.source?.sourceUrl,
      skuSource: p.skuSource,
      acknowledgedReviewFlags: p.acknowledgedReviewFlags,
    }).length > 0
  ) {
    gaps.push('identifierReview');
  }
  if (hasBlockingVerificationIssues(p)) gaps.push('verificationError');
  if (catalogContentConsistencyIssues(p).length > 0) gaps.push('contentConsistency');
  return gaps;
}

export function approvalGapCount(p: StagedLike): number {
  return getStagedApprovalGaps(p).length;
}
